import type { BudgetBand, EstimateInput, EstimateResult, ProjectType, QualificationResult } from './model';

type Range = [number, number];

const leadBase: Record<ProjectType, Range> = { integral: [60, 180], kitchen: [25, 75], bathroom: [25, 70], flooring: [12, 40], painting: [8, 28] };
const budget: Record<BudgetBand, Range | null> = { unknown: null, 'under-5k': [0, 5000], '5-15k': [5000, 15000], '15-40k': [15000, 40000], '40-90k': [40000, 90000], '90k-plus': [90000, 500000] };

export function qualifyLead(input: EstimateInput, estimate: EstimateResult, opts: { hasContact: boolean; privacyAccepted: boolean; shareAccepted: boolean; visitRequested: boolean }): QualificationResult {
  let score = 0;
  const reasons: string[] = [];
  if (opts.privacyAccepted && opts.shareAccepted) { score += 12; reasons.push('Consentimiento explícito.'); }
  if (opts.hasContact) { score += 15; reasons.push('Contacto accionable.'); }
  if (/^\d{5}$/.test(input.postalCode)) { score += 8; reasons.push('Zona identificada.'); }
  if (input.ownership === 'owner' || input.ownership === 'authorized') { score += 14; reasons.push('Decisor o autorizado.'); }
  else if (input.ownership === 'buying') score += 7;
  if (input.startWindow === 'now' || input.startWindow === '1-3') { score += 18; reasons.push('Plazo cercano.'); }
  else if (input.startWindow === '3-6') score += 10;
  const fit = getBudgetFit(input.budgetBand, estimate);
  if (fit === 'compatible') { score += 20; reasons.push('Presupuesto compatible.'); }
  else if (fit === 'posible') score += 10;
  else if (fit === 'desalineado') reasons.push('Presupuesto desalineado.');
  else score += 6;
  if (input.projectType === 'integral') score += 10;
  else if (input.projectType === 'kitchen' || input.projectType === 'bathroom') score += 6;
  if (opts.visitRequested) score += 5;
  score = Math.min(100, score);
  const tier = score >= 75 ? 'alta' : score >= 50 ? 'media' : 'baja';
  return { score, tier, reasons, budgetFit: fit, providerLeadValue: leadValue(input.projectType, tier, estimate.midpoint, fit) };
}

export function getBudgetFit(band: BudgetBand, estimate: EstimateResult): QualificationResult['budgetFit'] {
  const b = budget[band];
  if (!b) return 'desconocido';
  if (b[1] >= estimate.min && b[0] <= estimate.max) return 'compatible';
  if (b[1] >= estimate.min * 0.75 && b[0] <= estimate.max * 1.25) return 'posible';
  return 'desalineado';
}

function leadValue(type: ProjectType, tier: QualificationResult['tier'], midpoint: number, fit: QualificationResult['budgetFit']): QualificationResult['providerLeadValue'] {
  const tm = tier === 'alta' ? 1.25 : tier === 'media' ? 0.85 : 0.45;
  const bm = fit === 'compatible' ? 1.15 : fit === 'posible' ? 0.9 : fit === 'desalineado' ? 0.55 : 0.85;
  const vm = midpoint > 75000 ? 1.25 : midpoint > 35000 ? 1.1 : midpoint < 7500 ? 0.75 : 1;
  const ex = roundTo(mul(leadBase[type], tm * bm * vm), 5);
  const sh = roundTo(mul(ex, 0.35), 5);
  return { sharedMin: Math.max(5, sh[0]), sharedMax: Math.max(10, sh[1]), exclusiveMin: Math.max(10, ex[0]), exclusiveMax: Math.max(20, ex[1]), rationale: 'Captación alternativa, ticket, exclusividad, urgencia y encaje.' };
}
const mul = (r: Range, m: number): Range => [r[0] * m, r[1] * m];
const roundTo = (r: Range, n: number): Range => [Math.max(n, Math.round(r[0] / n) * n), Math.max(n, Math.round(r[1] / n) * n)];
